import { AutocompleteInputChangeReason } from "@mui/material/useAutocomplete";
import React from "react";
import { SegmentView } from "@tiny/api";
import {
  usePutSegmentFindFilterQuery,
  usePutSegmentFindIdsQuery,
} from "@tiny/api";
import { AutocompleteField, FormItem } from "@tiny/components";
import useDebounce from "@tiny/components/src/hooks/useDebounce";
import AudienceOption from "./AudienceOption/AudienceOption";
import { useStyles } from "./AudiencesValue.styles";
import { useWatch } from "react-hook-form";

interface AudiencesValueProps {
  prefixName: string;
  disabled?: boolean;
}

function AudiencesValue(props: AudiencesValueProps) {
  const { prefixName, disabled } = props;

  const name = `${prefixName}.value.value`;

  const criteria = useWatch({ name: `${prefixName}.criteria` });

  const rules: any = {
    required: {
      value: criteria === "NOT_IN",
      message: "Required field",
    },
  };

  return (
    <FormItem name={name} watchValue rules={rules}>
      <AudiencesInput disabled={disabled} criteria={criteria} />
    </FormItem>
  );
}

interface AudiencesInputProps {
  value?: string[];
  onChangeValue?: (value: string[]) => void;
  helperText?: string;
  error?: boolean;
  inputRef?: React.ForwardedRef<HTMLInputElement>;
  disabled?: boolean;
  criteria: string;
}

function AudiencesInput(props: AudiencesInputProps) {
  const {
    value,
    onChangeValue,
    helperText,
    error,
    inputRef,
    disabled,
    criteria,
  } = props;
  const classes = useStyles();

  const [internalAudienceValue, setInternalAudienceValue] =
    React.useState<SegmentView[]>();

  const [searching, setSearching] = React.useState(false);

  const [search, setSearch] = React.useState("");

  const searchDebounced = useDebounce(search, 500);

  const { data: { segments: audienceValue = [] } = {} } =
    usePutSegmentFindIdsQuery(
      {
        ids: value,
      },
      {
        enabled: !!value && value.length > 0 && !internalAudienceValue,
      }
    );

  const { data: { content: searchResult = [] } = {}, isLoading } =
    usePutSegmentFindFilterQuery(
      {
        searchTextName: searchDebounced,
        segmentTypes: ["UPLOAD", "INTEGRATION"],
      },
      {
        page: 0,
        size: 10,
      }
    );

  const changeInput = (
    _event: React.ChangeEvent<{}>,
    v: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (searching) {
      if (reason === "input") {
        setSearch(v);
      }
    } else {
      setSearch(v);
    }
  };

  const changeValue = (v: SegmentView[]) => {
    setInternalAudienceValue(v);
    const ids = v?.map((item) => item.id!);
    onChangeValue?.(ids);
  };

  const open = () => {
    setSearching(true);
  };

  const close = () => {
    setSearching(false);
  };

  const options: SegmentView[] = [
    // { id: "ALL", name: "All audience" },
    ...searchResult,
  ];

  const placeholder = React.useMemo(
    () =>
      ((internalAudienceValue || audienceValue)?.length || 0) === 0 &&
      criteria === "IN"
        ? "All Customers"
        : undefined,
    [audienceValue, internalAudienceValue, criteria]
  );

  return (
    <AutocompleteField
      className={classes.audiences}
      inputRef={inputRef}
      multiple
      placeholder={placeholder}
      loading={isLoading}
      options={options}
      inputValue={search}
      value={internalAudienceValue || audienceValue}
      onInputChange={changeInput}
      onChangeValue={changeValue}
      onOpen={open}
      onClose={close}
      getOptionLabel={(option) => option.name}
      limitTags={3}
      helperText={helperText}
      error={error}
      showHelperTextMargin
      disabled={disabled}
      getOptionSelected={(option, value) => {
        return option?.id === value?.id;
      }}
      renderOption={(
        optionProps: React.HTMLAttributes<HTMLLIElement>,
        option: SegmentView,
        state
      ) => (
        <li {...optionProps}>
          <AudienceOption option={option} state={state} />
        </li>
      )}
    />
  );
}

export default AudiencesValue;
